import React, {  useState } from "react";
import { WifiOff, RefreshCcw } from "lucide-react";
import { motion } from "framer-motion";

interface OfflineScreenProps {
  onRetry: () => void;
}

const OfflineScreen: React.FC<OfflineScreenProps> = ({ onRetry }) => {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = () => {
    setIsRetrying(true);
    setTimeout(() => {
      onRetry();
      setIsRetrying(false);
    }, 800);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 px-4"
    >
      <div className="flex flex-col items-center text-center max-w-md">
        <motion.div
          initial={{ scale: 0.8, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 0.1,
          }}
          className="relative flex items-center justify-center w-24 h-24 mb-8 bg-red-100 dark:bg-red-900/30 rounded-full"
        >
          <WifiOff className="w-12 h-12 text-red-500 dark:text-red-400" strokeWidth={1.5} />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-3 text-3xl font-bold tracking-tight text-gray-900 dark:text-white"
        >
          No Internet Connection
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8 text-lg text-gray-500 dark:text-gray-400"
        >
          Oops! It seems you're offline. Please check your network connection and try again.
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center justify-center px-8 py-3.5 text-base font-medium text-white transition-colors bg-orange-500 border border-transparent rounded-full shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed dark:focus:ring-offset-gray-900 min-w-[160px]"
        >
          <RefreshCcw
            className={`w-5 h-5 mr-2 ${isRetrying ? "animate-spin" : ""}`}
          />
          {isRetrying ? "Checking..." : "Try Again"}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default OfflineScreen;
